/**
 * sign-link-ceremony.ts — what the public ceremony SAYS, and which hash it signs.
 *
 * PURE MODULE: no DOM, no fetch, no `import.meta.env`. `SignLink` renders it; nothing in
 * here knows a page exists, which is what lets both decisions below be unit-tested.
 *
 * ── ONE PAGE, TWO CEREMONIES ──────────────────────────────────────────────────
 * `/sign/link` serves everybody who signs without an account, and `party` says which one
 * they are:
 *
 *   WITNESS  signs THE WITNESS AGREEMENT — their own instrument, not the hirer's. It
 *            carries the attestation about the hirer AND the terms it binds the witness to:
 *            being a point of contact, owing nothing financially, keeping what they learn
 *            confidential, and having their own contact details held under Act 843. The
 *            text is already frozen when they open it, because the WITNESS arm of
 *            `agreement_issue_token` freezes it against the hash the admin reviewed before
 *            the link is minted.
 *   DRIVER   the PAYER of a deposit — no contract, no vehicle, no account yet. They are
 *            the first signer, the wording is about their own deposit, and the identity
 *            step shows the payer name the admin entered when the link was made.
 *
 * THE WITNESS DOES NOT SIGN THE HIRE PURCHASE AGREEMENT. That instrument seats the driver
 * and Primelane alone; the witness signature that used to sit on it now lands on the witness
 * agreement, which is the document whose own clauses this ceremony has to be about.
 *
 * The template's own text is the operative document in both cases. Everything here is the
 * ceremony AROUND it: whose details are being confirmed, what is being confirmed, and how
 * long the reader insists on before it opens.
 *
 * ── WHY THE COPY IS DATA AND NOT JSX ──────────────────────────────────────────
 * Wording that varies by party, spread through a page as ternaries, is wording nobody can
 * review as a whole and no test can pin. Here it is one object per party, so a test reads
 * exactly what a payer is asked to confirm — and a statement added to one party cannot
 * silently leave the confirmation gate counting the other party's statements.
 */

// ─── The party ───────────────────────────────────────────────────────────────

export type SignLinkParty = 'WITNESS' | 'DRIVER';

/**
 * The party a link is for, normalized.
 *
 * UNKNOWN FALLS BACK TO WITNESS, deliberately. `party` is new on the payload and the
 * witness link is the one that predates it, so an endpoint that has not been redeployed
 * yet answers without the key — and a witness link must keep working through that window.
 * The fallback is also the conservative direction: witness wording asks somebody to
 * confirm they know the hirer and accept the duties a witness takes on, which nobody signs
 * by accident, whereas payer wording would assert a payment they may not have made.
 */
export function normalizeSignLinkParty(value: string | null | undefined): SignLinkParty {
    return String(value ?? '').trim().toUpperCase() === 'DRIVER' ? 'DRIVER' : 'WITNESS';
}

// ─── The hash that gets attested ─────────────────────────────────────────────

export type HashBlock = 'MISMATCH' | 'UNVERIFIABLE';

export interface HashAttestation {
    /** The hash to submit with the signature, or null when signing must be blocked. */
    hash: string | null;
    blocked: HashBlock | null;
}

/**
 * Which hash this signature attests to — the one decision on this page that is not copy.
 *
 * `agreement_token_lookup` HASHES EXACTLY THE BYTES IT SERVES, on both arcs — the frozen
 * ones for a witness, and a live render for a payer whose document nothing has frozen yet.
 * So `content_sha256` is normally present either way, and the server's hash is the one
 * submitted.
 *
 * THAT IS NOT THE SAME AS ECHOING IT UNEXAMINED. The digest this browser took over the
 * bytes it actually displayed is compared against it first, and any disagreement BLOCKS
 * rather than being papered over: it means the text on screen is not the text the server
 * hashed, and on the payer's arc it is that submitted hash `agreement_freeze_if_needed`
 * seals the document against forever.
 *
 * THE LOCAL DIGEST SUBSTITUTES FOR A MISSING SERVER HASH ON THE PAYER'S ARC ONLY, and that
 * branch exists for one window: an endpoint deployed before `content_sha256` was projected.
 * A payer freezes the document with what they submit, so their own digest is a truthful
 * attestation. A WITNESS is never upgraded to one — a frozen document that arrived without
 * its hash is a broken link, and accepting a client digest there would let a
 * served-anything payload be signed as if it were the record.
 */
export function hashAttestation(
    party: SignLinkParty,
    serverHash: string | null | undefined,
    localDigest: string | null | undefined,
): HashAttestation {
    const server = serverHash ?? null;
    const local = localDigest ?? null;

    if (server !== null && local !== null && server !== local) {
        return { hash: null, blocked: 'MISMATCH' };
    }
    if (server !== null) return { hash: server, blocked: null };
    if (party === 'DRIVER' && local !== null) return { hash: local, blocked: null };
    return { hash: null, blocked: 'UNVERIFIABLE' };
}

// ─── The copy ────────────────────────────────────────────────────────────────

/** One line of the "are these your details?" list. Absent values are not listed. */
export interface CeremonyRow {
    label: string;
    value: string;
}

/** One tick-box on the confirmation step. */
export interface CeremonyStatement {
    title: string;
    detail: string;
}

export interface SignLinkFacts {
    /** Whoever is being asked to sign: `signer_name`, or the witness name for old payloads. */
    signerName: string | null;
    driverFirstName: string | null;
    roleLabel: string | null;
    vehicleRegistration: string | null;
    referenceNo: string | null;
    /** Already formatted for display — this module never touches a clock. */
    expiresLabel: string | null;
    consentVersion: string;
}

export interface SignLinkCeremony {
    party: SignLinkParty;
    /** localStorage namespace for the draft. Stable per party, so no draft is orphaned. */
    draftPrefix: string;
    /** Seconds the reader step insists on. */
    readSecondsFloor: number;
    /** Used when the payload carries no title of its own. */
    fallbackTitle: string;
    /** The name shown when the payload names nobody. */
    signerName: string;
    identity: {
        eyebrow: string;
        heading: string;
        lede: string;
        cta: string;
        note: string;
        foot: string;
    };
    identityRows: CeremonyRow[];
    confirm: {
        eyebrow: string;
        heading: string;
        cta: string;
        allNote: string;
        someNote: string;
    };
    statements: CeremonyStatement[];
    reader: {
        note: string;
        notice: string;
    };
    sign: {
        eyebrow: string;
        heading: string;
        submit: string;
        signingAsSub: string;
        typedNameLabel: string;
        note: string;
    };
    done: {
        toast: string;
        heading: string;
        body: string;
        signatureAlt: string;
    };
}

/** Rows whose value is missing are dropped: a pre-contract deposit has no vehicle. */
function rows(entries: readonly (readonly [string, string | null])[]): CeremonyRow[] {
    const out: CeremonyRow[] = [];
    for (const [label, value] of entries) {
        const text = (value ?? '').trim();
        if (text.length > 0) out.push({ label, value: text });
    }
    return out;
}

const COUNT_WORDS = [
    'none',
    'one',
    'two',
    'three',
    'four',
    'five',
    'six',
    'seven',
    'eight',
    'nine',
    'ten',
] as const;

/**
 * The footnote under the tick-boxes, COUNTED FROM THE STATEMENTS rather than written next
 * to them.
 *
 * The parties do not confirm the same number of things — a payer accepts terms about their
 * own money that a witness is not party to — and "Confirm all four to continue." under five
 * boxes is the kind of wrong that a reader trusts and no renderer complains about. Deriving
 * it is what makes a statement added to one party impossible to get wrong here.
 */
function confirmNotes(count: number): { allNote: string; someNote: string } {
    const word = COUNT_WORDS[count] ?? String(count);
    return {
        allNote: `All ${word} confirmed.`,
        someNote: `Confirm all ${word} to continue.`,
    };
}

/**
 * BOTH LINK PARTIES SIGN AN INSTRUMENT THAT BINDS THEM, so both read on the same floor as
 * the in-app driver ceremony.
 *
 * The witness floor used to be 30 because a witnessing was held to be shorter than a
 * hire-purchase read and the witness was held not to be bound by the text. Neither half
 * survives the witness agreement: clause 3 puts the witness on the hook as a point of
 * contact, clause 4 tells them what they are NOT responsible for and clause 6 binds them to
 * confidentiality. Somebody taking on named duties reads for as long as somebody accepting
 * terms about their own money.
 *
 * 45 SECONDS SURVIVES THE REWRITE, AS A RULING RATHER THAN AS A DEFAULT. Both bodies grew
 * — the witness agreement to seven pages, the deposit agreement to five — and the owner kept
 * both floors where they are (2026-08-13 packet rulings). Raising them is therefore a
 * decision to be asked for, not one to be inferred from a longer document.
 *
 * Two constants rather than one shared value: they are two decisions that happen to agree
 * today, and collapsing them would make a change to either look like a change to both.
 */
const READ_SECONDS_WITNESS = 45;
const READ_SECONDS_PAYER = 45;

export function signLinkCeremony(party: SignLinkParty, facts: SignLinkFacts): SignLinkCeremony {
    const driverFirst = (facts.driverFirstName ?? '').trim() || 'the hirer';
    const consent = `Consent version ${facts.consentVersion}`;

    if (party === 'DRIVER') {
        const payer = (facts.signerName ?? '').trim() || 'Payer';
        const role = (facts.roleLabel ?? '').trim() || 'Payer of the deposit';

        // EVERY STATEMENT HERE IS ONE THE DEPOSIT AGREEMENT ITSELF MAKES, and none of them
        // restates a figure. Clause 1.1 is the payment; clause 2 is what paying does and
        // what it expressly does NOT do; clause 3 is the five-limb refundability ladder;
        // clause 4.1 is where a refund goes; and the closing confirmation block is the four
        // bullets a driver signs under.
        //
        // THE LADDER MOVED, AND THE OLD TICK-BOX WOULD NOW BE FALSE. The superseded body
        // made the deposit non-refundable "once vehicle allocation or onboarding has
        // started". Clause 3 moves that line to ACCEPTANCE: 3.2 keeps it refundable while
        // the driver withdraws before Primelane has confirmed an allocation (less a
        // third-party cost they approved in advance), and only 3.3 — a confirmed allocation
        // the driver has ACCEPTED and then declines — takes it away. A payer ticking the old
        // sentence would be confirming a worse bargain than the document gives them, which
        // is the one direction this copy must never err in. It is split across two boxes
        // because a ladder compressed into one is a ladder nobody reads.
        //
        // THE AMOUNT AND THE DATE ARE POINTED AT, NOT REPRINTED. The lookup payload carries
        // no financial value by design, so a tick-box that quoted a figure would either
        // need a new field on a deliberately thin payload or invent one. Pointing at the
        // text the payer has just read costs nothing and keeps the ceremony downstream of
        // the operative document, which is where it belongs.
        const statements: CeremonyStatement[] = [
            {
                title: `I am ${payer}.`,
                detail: 'The person this deposit agreement is with.',
            },
            {
                title: 'I have paid the deposit this agreement states, on the date it states.',
                detail: 'The amount and the payment date are printed in the agreement you have just read.',
            },
            // Clause 2, both halves. The one a payer is likeliest to have assumed wrong is
            // 2.2's first limb — that the money holds a particular vehicle for them — so it
            // is the title, and the limbs that work in their favour are the detail.
            {
                title: 'I understand this deposit does not reserve a particular vehicle.',
                detail:
                    'It confirms I want to take part and lets allocation and onboarding ' +
                    'begin. It counts towards what I pay if I take a vehicle on hire ' +
                    'purchase, and no weekly payment starts until one is handed over.',
            },
            {
                title:
                    'I understand the deposit is refundable until I accept a vehicle ' +
                    'Primelane has allocated.',
                detail:
                    'In full if Primelane cannot proceed with my application through no ' +
                    'fault of mine; less any third-party cost I approved in advance if I ' +
                    'withdraw first. A refund goes back to the account or number it came from.',
            },
            {
                title:
                    'I understand it stops being refundable once I accept an allocated ' +
                    'vehicle and then decide not to proceed.',
                detail:
                    'And that it may not be refundable if Primelane cancels my application ' +
                    'because I gave false information or did not complete onboarding.',
            },
            // NOT "the name and phone number": the particulars now carry a residential
            // address as well, and that row is offered rather than demanded on an attached
            // deposit — so a box naming the three fields would ask a payer to confirm one
            // that may not be printed. The payload cannot tell which, and "the details this
            // agreement shows" is true either way.
            {
                title: 'The details this agreement shows for me are correct.',
                detail: role,
            },
            {
                title: 'I understand this has the same legal effect as signing on paper.',
                detail: consent,
            },
        ];

        return {
            party,
            // Not `witness:` — a payer's draft has to be its own, and a witness's in-flight
            // draft keeps the namespace it was saved under.
            draftPrefix: 'payer',
            readSecondsFloor: READ_SECONDS_PAYER,
            // TITLE CASE, BECAUSE THAT IS THE REGISTRY'S. `agreement_templates.title` reads
            // "Deposit Agreement" and the payload carries it, so this literal is only reached
            // when the lookup returned none — and a fallback that is the one place the
            // instrument is named differently is a fallback nobody would notice was wrong.
            fallbackTitle: 'Deposit Agreement',
            signerName: payer,
            identity: {
                eyebrow: 'Step 1 · Confirm who you are',
                heading: 'Are these your details?',
                lede: 'These are the details Primelane recorded for the person paying this deposit.',
                cta: 'Yes, these are my details',
                note: 'Signing takes about five minutes.',
                foot: 'Something wrong? Contact Primelane and they will correct it before you sign.',
            },
            identityRows: rows([
                ['Full name', payer],
                ['Your role', role],
                ['Vehicle', facts.vehicleRegistration],
                ['Agreement', facts.referenceNo],
                ['Link expires', facts.expiresLabel],
            ]),
            confirm: {
                eyebrow: 'Step 3 · Your confirmation',
                heading: 'Confirm each statement',
                cta: 'Continue to signing',
                ...confirmNotes(statements.length),
            },
            statements,
            reader: {
                note: 'This is the agreement you are signing, so please read all of it.',
                // No "locked" claim: nothing is frozen until this signature freezes it.
                notice: 'This exact text is what you are signing. It locks the moment you sign.',
            },
            sign: {
                eyebrow: 'Step 4 · Sign the agreement',
                heading: 'Sign with your finger',
                submit: 'Submit your signature',
                signingAsSub: role,
                typedNameLabel: 'Type your full name',
                note: 'Recorded with your name, the time and the version of the document you read.',
            },
            done: {
                toast: 'Signature recorded',
                heading: 'Signature recorded',
                // "READ IT HERE", not "come back for your copy": the executed agreement is
                // opened on this page and never handed over as a file (owner ruling,
                // 2026-08-21). The link is still what a payer with no account holds, so the
                // sentence keeps saying that it goes on working.
                body: 'Thank you. You can close this page — Primelane countersigns next and issues the final deposit agreement. This link keeps working until it expires, so you can come back and read your copy here.',
                signatureAlt: 'Your signature',
            },
        };
    }

    const witness = (facts.signerName ?? '').trim() || 'Witness';
    // THE SERVER'S LABEL WINS. `agreement_token_lookup` projects the role_label off the
    // pinned template roster, which on the witness agreement reads "The Witness"; the
    // literal below is only what a payload that carried none falls back to.
    const role = (facts.roleLabel ?? '').trim() || 'Witness to the hirer';

    // EVERY STATEMENT HERE IS ONE THE WITNESS AGREEMENT ITSELF MAKES, which is a change of
    // subject and not a change of tone: the witness signature moved off the hire purchase
    // agreement onto this instrument, so the ceremony has to be about THIS instrument's
    // clauses rather than about somebody else's signature.
    //
    // CLAUSE NUMBERS ARE THIS BODY'S, RE-DERIVED. Clause 2 is the attestation, clause 3 the
    // point-of-contact role, clause 4 what the witness is NOT responsible for, clause 5 the
    // witness's own information (5.1 the particulars, 5.2-5.4 Act 843), clause 6
    // confidentiality. They are held fixed by the drafting pass precisely because this
    // ceremony maps topics onto them.
    //
    // THE ATTESTATION NARROWED, AND THE COPY HAD TO NARROW WITH IT. The superseded body had
    // the witness attest to the driver's "identity, good character, and general conduct"
    // and to their "full understanding" of what they signed. Clause 2.1 now reaches identity
    // and the information the driver gave; 2.3 says "an understanding", not full; and 2.5
    // states outright that the witness confirms neither character nor ability to pay. Copy
    // that kept the old reach would collect a wider attestation than the document takes —
    // and it is the witness who would carry it.
    //
    // The rest are the terms the agreement binds the WITNESS to, and they are the reason the
    // read floor is what it is: clause 3 makes them a point of contact, clause 4 is the one a
    // witness most needs to have read (it is the clause that says they owe nothing if the
    // hirer defaults, and the fear that they do is why witnesses hesitate), clause 5 puts a
    // duty to keep their details current on them and states what Primelane may do with those
    // details, and clause 6 binds them to confidentiality.
    const statements: CeremonyStatement[] = [
        {
            title: `I am ${witness}.`,
            detail: 'The person named as the Witness on this agreement.',
        },
        // §2.1, §2.2 AND THE LIMIT AT §2.5, IN ONE BOX, because they are one attestation:
        // what the witness knows of the hirer, and what they are NOT being asked to vouch
        // for. The old detail ("in person, or by a video call where I could see them")
        // described a verification method no clause of this agreement asks for.
        {
            title: `I know ${driverFirst} personally, and I confirm their identity.`,
            detail:
                'And that what they told Primelane about themselves is true, so far as I ' +
                'know. I am not asked to vouch for their character or their ability to pay.',
        },
        // §2.3 IN THE SECOND PERSON, AND NO WIDER — the one attestation the witnessing is
        // actually for. "An understanding", which is the clause's own word since this pass;
        // "full understanding" was the superseded body's and is a stronger thing to swear to.
        // §2.4 is conditional on the witness having been present, so it is stated
        // conditionally.
        {
            // "I confirm", because that is the clause's own verb — and because a title
            // opening on the name reads as a lowercase sentence the moment the payload
            // carries none and the fallback "the hirer" lands in front.
            title: `I confirm ${driverFirst} signed the hire purchase agreement voluntarily.`,
            detail:
                'With an understanding of its terms, obligations and consequences — and, if ' +
                'I was there when they signed, that they had the chance to read it and ask ' +
                'questions first.',
        },
        // §5.1 — the particulars are the witness's own assertion, and the clause attaches an
        // ongoing duty to them: tell Primelane when a number or an address changes. The duty
        // is in the title because a detail line is where the role label has to live.
        {
            title:
                'The details shown about me are correct, and I will tell Primelane if my ' +
                'address or phone number changes.',
            detail: role,
        },
        // §3, ALL THREE OF ITS SUB-CLAUSES: the contact role (3.1), the information limbs
        // (3.2), and the duty to speak up if something has happened to the hirer (3.3). The
        // "information and help only" line is the boundary §4.3 draws — nothing in this
        // agreement requires the witness to recover the vehicle, take possession of it, be put
        // at risk or perform any of the driver's obligations — and not a fourth sub-clause of
        // §3, which the published body does not have.
        {
            title: `I agree to be a point of contact if Primelane cannot reach ${driverFirst}.`,
            detail:
                'And to pass on what I reasonably can to help them make contact, or if I ' +
                'learn something has happened to the hirer. This asks for information and ' +
                'help only.',
        },
        // SCOPED AS §4 SCOPES IT, which is narrower than the reassurance it is tempting to
        // give: the clause disclaims liability for "the Driver's obligations under the Hire
        // Purchase Agreement", not for anything the hirer might ever owe anybody. A tick-box
        // that widened it would have the witness confirm a protection the document does not
        // grant — the one direction this copy must never err in. 4.1 (not a guarantor) and
        // 4.2's list of what does not fall on them are the detail.
        {
            title:
                'I understand I am not financially responsible for what the hirer owes under ' +
                'the hire purchase agreement.',
            detail:
                'I am not a guarantor: no instalment, no interest on a late payment, no ' +
                'repair or recovery cost and no penalty under that agreement falls on me if ' +
                'the hirer defaults.',
        },
        // §6. The carve-out is §6.2's — the law, or an authority — and not the superseded
        // body's "reasonably requested by Primelane", which this pass dropped.
        {
            title: 'I will keep what I learn in this role confidential.',
            detail: 'Unless the law requires the disclosure, or it is made to an authority.',
        },
        // §5.2-5.4, NEW IN THIS BODY AND EARNING A BOX OF ITS OWN. This is the instrument
        // that takes a THIRD PARTY'S home address and two telephone numbers, and the witness
        // gets nothing out of the transaction — so what Primelane may do with those details,
        // and the fact that they are never sold or passed on for marketing, is material to
        // whether somebody agrees to hand them over at all. It is a protection rather than a
        // duty, which is the same reason clause 4 is on this list.
        {
            title:
                'I understand Primelane holds my contact details, and uses them only to ' +
                'contact me about this agreement.',
            detail:
                "Not sold, and not released for anybody else's marketing. I can ask what is " +
                'held about me and have anything wrong corrected, under the Data Protection ' +
                'Act, 2012.',
        },
        {
            title: 'I understand this has the same legal effect as signing on paper.',
            detail: consent,
        },
    ];

    return {
        party,
        // Still `witness:` — the party is the same person and an in-flight draft keeps the
        // namespace it was saved under, whichever instrument they are on.
        draftPrefix: 'witness',
        readSecondsFloor: READ_SECONDS_WITNESS,
        // Title Case for the same reason as the payer's: `agreement_templates.title` is
        // "Witness Agreement".
        fallbackTitle: 'Witness Agreement',
        signerName: witness,
        identity: {
            eyebrow: 'Step 1 · Confirm who you are',
            heading: 'Are these your details?',
            lede: 'These are the details printed on the witness agreement you are signing.',
            cta: 'Yes, these are my details',
            note: 'Signing takes about five minutes.',
            foot: 'Something wrong? Contact Primelane and they will correct it before you sign.',
        },
        identityRows: rows([
            ['Full name', witness],
            ['Your role', role],
            ['Witness for', driverFirst],
            ['Vehicle', facts.vehicleRegistration],
            ['Agreement', facts.referenceNo],
            ['Link expires', facts.expiresLabel],
        ]),
        confirm: {
            // Not "Your attestation" any more: half of what is confirmed below is an
            // undertaking this agreement asks of the witness, not an attestation about
            // somebody else.
            eyebrow: 'Step 3 · Your confirmation',
            heading: 'Confirm each statement',
            cta: 'Continue to signing',
            ...confirmNotes(statements.length),
        },
        statements,
        reader: {
            // NOT "exactly what {driver} signed" — that was true of the hire purchase
            // agreement and is false of this one, which the hirer never signs. The lock is
            // real though: the WITNESS arm of `agreement_issue_token` freezes the text
            // against the reviewed hash before the link exists.
            note: 'This is the agreement you are signing, so please read all of it.',
            notice: 'Locked — this is the witness agreement you are signing.',
        },
        sign: {
            eyebrow: 'Step 4 · Sign as witness',
            heading: 'Sign with your finger',
            submit: 'Submit witness signature',
            signingAsSub: `Witness to ${driverFirst}`,
            typedNameLabel: 'Type your full name',
            note: 'Recorded with your name, the time and the version of the document you read.',
        },
        done: {
            toast: 'Witness signature recorded',
            heading: 'Witness signature recorded',
            // THE INSTRUMENT IN HAND, AND NOTHING ELSE. The old wording issued "the final
            // document" to the hirer, which was the shape of a witness signing the hirer's
            // agreement; naming the hirer's hire purchase agreement instead is wrong in the
            // other direction, and wrong on most contracts — the hire purchase agreement is
            // on paper for the majority of live ones, so there is no in-app document to
            // countersign at all, and this token's payload could not tell either way.
            // Read here, for the reason the payer's own done body states.
            body: 'Thank you. You can close this page — Primelane countersigns this witness agreement next. This link keeps working until it expires, so you can come back and read your own copy here.',
            signatureAlt: 'Your witness signature',
        },
    };
}
